/**
 * Text Formatting Utilities
 * 
 * A collection of reusable text formatting functions for consistent
 * text processing across the application.
 */

import React from "react";

/**
 * Converts URLs in text to clickable hyperlinks
 * 
 * @param {string} text - The text to process
 * @param {Object} options - Configuration options
 * @param {string} options.className - CSS classes to apply to links
 * @param {boolean} options.openInNewTab - Whether to open links in new tab (default: true)
 * @returns {React.ReactNode} - Text with linkified URLs or original text
 * 
 * @example
 * linkifyText("Visit www.example.com for more info")
 * // Returns: "Visit <a href="https://www.example.com">www.example.com</a> for more info"
 * 
 * @example
 * linkifyText("Check https://example.com", { className: "custom-link" })
 */
export const linkifyText = (text, options = {}) => {
  const {
    className = "text-blue-600 dark:text-blue-400 hover:underline break-all",
    openInNewTab = true,
  } = options;

  if (!text || text === "—" || text === "") return text;
  
  // Regular expression to match URLs
  // Matches: https://example.com, http://example.com, www.example.com, example.com
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9][a-zA-Z0-9-]+\.[a-zA-Z]{2,}[^\s]*)/g;
  
  const parts = text.split(urlRegex);
  
  return parts.map((part, index) => {
    if (part.match(urlRegex)) {
      // Ensure the URL has a protocol
      let href = part;
      if (!part.startsWith('http://') && !part.startsWith('https://')) {
        href = 'https://' + part;
      }
      
      return (
        <a
          key={index}
          href={href}
          target={openInNewTab ? "_blank" : "_self"}
          rel={openInNewTab ? "noopener noreferrer" : undefined}
          className={className}
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

/**
 * Converts email addresses in text to mailto links
 * 
 * @param {string} text - The text to process
 * @param {string} className - CSS classes to apply to email links
 * @returns {React.ReactNode} - Text with linkified emails or original text
 * 
 * @example
 * linkifyEmails("Contact us at support@example.com")
 */
export const linkifyEmails = (text, className = "text-blue-600 hover:underline") => {
  if (!text || text === "—" || text === "") return text;
  
  // Regular expression to match email addresses
  const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/g;
  
  const parts = text.split(emailRegex);
  
  return parts.map((part, index) => {
    if (part.match(emailRegex)) {
      return (
        <a
          key={index}
          href={`mailto:${part}`}
          className={className}
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

/**
 * Converts phone numbers in text to tel links
 * 
 * @param {string} text - The text to process
 * @param {string} className - CSS classes to apply to phone links
 * @returns {React.ReactNode} - Text with linkified phone numbers or original text
 * 
 * @example
 * linkifyPhones("Call us at (555) 123-4567")
 */
export const linkifyPhones = (text, className = "text-blue-600 hover:underline") => {
  if (!text || text === "—" || text === "") return text;
  
  // Regular expression to match phone numbers (various formats)
  const phoneRegex = /(\+?1?[-.\s]?\(?[0-9]{3}\)?[-.\s]?[0-9]{3}[-.\s]?[0-9]{4})/g;
  const parts = text.split(phoneRegex);
  
  return parts.map((part, index) => {
    if (part.match(phoneRegex)) {
      const cleanPhone = part.replace(/[^0-9+]/g, '');
      return (
        <a
          key={index}
          href={`tel:${cleanPhone}`}
          className={className}
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

/**
 * Converts all URLs, emails, and phone numbers to clickable links
 * 
 * @param {string} text - The text to process
 * @param {Object} options - Configuration options
 * @returns {React.ReactNode} - Text with all linkified content
 * 
 * @example
 * linkifyAll("Email: contact@example.com | Web: www.example.com | Phone: (555) 123-4567")
 */
export const linkifyAll = (text, options = {}) => {
  if (!text || text === "—" || text === "") return text;
  
  // Apply in order: URLs first, then emails, then phones
  let result = linkifyText(text, options);
  // Note: For emails and phones, we'd need more complex logic to avoid
  // re-processing already linkified content. For now, keeping it simple.
  return result;
};

/**
 * Truncates text to a specified length and adds ellipsis
 * 
 * @param {string} text - The text to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @param {string} suffix - Suffix to add when truncated (default: "...")
 * @returns {string} - Truncated text
 * 
 * @example
 * truncateText("This is a very long text", 10)
 * // Returns: "This is a..."
 */
export const truncateText = (text, maxLength, suffix = "...") => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + suffix;
};

/**
 * Formats text with preserved whitespace and newlines
 * 
 * @param {string} text - The text to format
 * @param {Object} options - Configuration options
 * @returns {React.ReactNode} - Formatted text component
 * 
 * @example
 * formatPreservedText("Line 1\nLine 2\nLine 3")
 */
export const formatPreservedText = (text, options = {}) => {
  const { linkify = false, className = "" } = options;
  
  if (!text || text === "—") return text;
  
  const content = linkify ? linkifyText(text) : text;
  
  return (
    <span className={`whitespace-pre-wrap ${className}`}>
      {content}
    </span>
  );
};